import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  LinkIcon,
  CreditCardIcon,
  UsersIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/authStore';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: string[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin', icon: HomeIcon, roles: ['ADMIN', 'FINANCE', 'STAFF'] },
  { name: 'Payment Links', href: '/admin/payment-links', icon: LinkIcon, roles: ['ADMIN', 'FINANCE', 'STAFF'] },
  { name: 'Templates', href: '/admin/templates', icon: DocumentDuplicateIcon, roles: ['ADMIN', 'FINANCE', 'STAFF'] },
  { name: 'Transactions', href: '/admin/transactions', icon: CreditCardIcon, roles: ['ADMIN', 'FINANCE'] },
  { name: 'Users', href: '/admin/users', icon: UsersIcon, roles: ['ADMIN'] },
  { name: 'Reports', href: '/admin/reports', icon: ChartBarIcon, roles: ['ADMIN', 'FINANCE'] },
  { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon, roles: ['ADMIN'] },
];

export function Sidebar() {
  const user = useAuthStore((state) => state.user);

  const filteredNavigation = navigation.filter(
    (item) => user && item.roles.includes(user.role)
  );

  return (
    <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200">
      {/* Logo */}
      <div className="flex items-center h-16 px-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">H</span>
          </div>
          <span className="font-semibold text-gray-900">Hotel101 Payments</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {filteredNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/admin'}
            className={({ isActive }) =>
              clsx(
                'flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150',
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              )
            }
          >
            <item.icon className="w-5 h-5 mr-3" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary font-medium">
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
